import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Paper, Container, Button, useTheme, Box, Switch } from '@mui/material';
import { useAccount, useChainId, useBalance } from 'wagmi';
import toast from 'react-hot-toast';
import { Address } from 'viem';
import SwapHorizontalCircleIcon from '@mui/icons-material/SwapHorizontalCircle';

import TokenInput from '../swap/TokenInput';
import { OrderCondition } from '../swap/OrderCondition';
import TokenSelectionDialog from '../dialogs/TokenSelectionDialog';
import SwapConfirmDialog from '../dialogs/SwapConfirmDialog';
import SwapProgressingDialog from '../dialogs/SwapProgressingDialog';
import useAppContext from '../../lib/hooks/useAppContext';
import { getRouterContract, swapTokens } from '../../lib/utils/trade';
import { useEthersSigner } from '../../lib/hooks/useSigner';
import { ARBITRUM_DEMO_TOKEN_LIST, ChainIds } from '../../constants';
import { COLORS } from '../../theme';
import { Direction } from '../../types';
import UnlockedImage from '../../assets/unlocked_by_fairblock.png';
import { SwapInstruction } from '../swap/SwapInstruction';

const SwapCard: React.FC = () => {
  const {
    setTokens,
    fromToken,
    toToken,
    setFromToken,
    setToToken,
    amountFrom,
    setAmountFrom,
    setAmountTo,
    amountTo,
    tokenSelectionDialog,
    poolInfo,
  } = useAppContext();
  const theme = useTheme();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { data: fromBalanceData } = useBalance({
    address,
    ...(fromToken && !fromToken.native
      ? { token: fromToken?.address as Address }
      : {}),
  });
  const signer = useEthersSigner();

  const [openSwapConfirmDlg, setOpenSwapConfirmDlg] = useState<boolean>(false);
  const [openSwapProcessingDlg, setOpenSwapProcessingDlg] =
    useState<boolean>(false);
  const [isEncrypted, setIsEncrypted] = useState<boolean>(true);

  const [success, setSuccess] = useState<boolean>(false);

  const [orderValue, setOrderValue] = useState<number>(1);
  const [poolInfoDirection, setPoolInfoDirection] = useState(Direction.default);

  const initializeTokens = () => {
    setTokens(ARBITRUM_DEMO_TOKEN_LIST);
    setFromToken(ARBITRUM_DEMO_TOKEN_LIST[0]);
    setToToken(null);
    setAmountFrom('0.0');
    setAmountTo('0.0');
  };

  useEffect(() => {
    initializeTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);

  const handleClickSwap = () => {
    // show swap confirm dialog
    setOpenSwapConfirmDlg(true);
  };

  const handlePoolInfoChangeDirection = () => {
    setPoolInfoDirection((prev) =>
      prev === Direction.default ? Direction.reverted : Direction.default,
    );
  };

  const renderSwapButton = () => {
    let btnDisabled = false;
    let btnTxt = 'Swap';
    if (!isConnected) {
      btnDisabled = true;
      btnTxt = 'Connect Wallet';
    } else if (!fromToken || !toToken) {
      btnDisabled = true;
      btnTxt = 'Select Tokens';
    } else if (
      fromBalanceData?.formatted &&
      parseFloat(amountFrom) > parseFloat(fromBalanceData?.formatted)
    ) {
      btnDisabled = true;
      btnTxt = 'Insufficient Balance';
    } else if (parseFloat(amountFrom) <= 0) {
      btnDisabled = true;
      btnTxt = 'Invalid Amount';
    }

    return (
      <Button
        variant="contained"
        sx={{
          width: '100%',
          mt: 2,
          borderRadius: 2,
          '&.Mui-disabled': {
            background: '#1a202c',
            color: '#c0c0c0',
            cursor: 'not-allowed',
          },
        }}
        disabled={btnDisabled}
        onClick={handleClickSwap}
      >
        {btnTxt}
      </Button>
    );
  };

  const renderPoolInfo = () => {
    if (!fromToken || !toToken) {
      return null;
    }

    if (!poolInfo.length) return null;

    const price =
      poolInfoDirection === Direction.default
        ? parseFloat(poolInfo[1]) / parseFloat(poolInfo[0])
        : parseFloat(poolInfo[0]) / parseFloat(poolInfo[1]);
    return (
      <Box
        mt={2}
        sx={{ borderRadius: 3, border: '1px solid #1a202c' }}
        color="#ccc"
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          px={2}
          py={1}
          fontSize={12}
        >
          Market Price
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          pb={1}
          fontSize={12}
        >
          <Box display="flex" alignItems="center">
            {`1 ${poolInfoDirection === Direction.default ? fromToken.symbol : toToken.symbol} = ${price.toFixed(4).replace(/\.0+$/, '')} ${poolInfoDirection === Direction.default ? toToken.symbol : fromToken.symbol}`}
          </Box>
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              ml: 2,
              ':hover': { color: COLORS.fairblock },
            }}
            alignItems="center"
            onClick={() => handlePoolInfoChangeDirection()}
          >
            <SwapHorizontalCircleIcon fontSize="small" />
          </Box>
        </Box>
      </Box>
    );
  };

  const onClickConfirm = async () => {
    if (!signer || !address || !fromToken || !toToken) return;
    const routerContract = getRouterContract(
      chainId || ChainIds.ARBITRUM_DEMO,
      signer,
    );

    const price =
      poolInfoDirection === Direction.default
        ? parseFloat(poolInfo[1]) / parseFloat(poolInfo[0])
        : parseFloat(poolInfo[0]) / parseFloat(poolInfo[1]);

    setOpenSwapConfirmDlg(false);
    try {
      setOpenSwapProcessingDlg(true);
      setSuccess(false);
      await swapTokens(
        fromToken,
        toToken,
        amountFrom,
        routerContract,
        address,
        signer,
        price,
        isEncrypted,
        orderValue,
      );
      toast.success('Token swapped successfully!');
      setSuccess(true);
      setOpenSwapProcessingDlg(false);
    } catch (err) {
      toast.error('Something went wrong! Please try again!');
      setSuccess(false);
      setOpenSwapProcessingDlg(false);
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        [theme.breakpoints.down('md')]: {
          px: 0,
        },
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        gap={2}
        justifyContent="center"
      >
        <Box>
          <Paper
            elevation={5}
            sx={{
              px: 2,
              py: 2,
              borderRadius: 3,
            }}
          >
            {/* <Typography
              variant="h6"
              component="h2"
              gutterBottom
              textAlign="center"
            >
              Time Encrypted Limit
            </Typography> */}
            <TokenInput position="from" />
            <TokenInput position="to" />
            <OrderCondition
              orderValue={orderValue}
              setOrderValue={setOrderValue}
            />
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ bgcolor: '#1a202c', borderRadius: 3 }}
              color="#ccc"
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                px={2}
                py={1}
              >
                Encrypt
              </Box>
              <Box display="flex" justifyContent="center">
                <Switch
                  checked={isEncrypted}
                  onChange={(e) => setIsEncrypted(e.target.checked)}
                />
              </Box>
            </Box>

            {renderSwapButton()}
            {/* {renderPriceInfo()} */}
            {renderPoolInfo()}
          </Paper>
          {success && (
            <Paper
              elevation={5}
              sx={{ mt: 3, px: 2, py: 2, borderRadius: 3, color: '#cacaca' }}
            >
              Successfully submitted your limit order. You can track your swaps{' '}
              <Link
                to="/track"
                style={{
                  textDecoration: 'none',
                  color: '#42a5f5',
                }}
              >
                here
              </Link>
              .
            </Paper>
          )}
        </Box>
        <Box
          width="55%"
          sx={{
            [theme.breakpoints.down('md')]: {
              width: '100%',
            },
          }}
        >
          <SwapInstruction />
        </Box>
      </Box>
      <Box
        sx={{
          [theme.breakpoints.up('md')]: {
            position: 'absolute',
            bottom: 5,
            left: 0,
            right: 0,
            mt: 0,
          },
          mx: 'auto',
          width: 100,
          mt: 5,
        }}
      >
        <img src={UnlockedImage} alt="unlocked" width={100} height={100} />
      </Box>
      {tokenSelectionDialog.open && <TokenSelectionDialog />}
      {openSwapConfirmDlg && (
        <SwapConfirmDialog
          open={openSwapConfirmDlg}
          onClose={() => setOpenSwapConfirmDlg(false)}
          onOk={onClickConfirm}
        />
      )}
      {openSwapProcessingDlg && (
        <SwapProgressingDialog
          open={openSwapProcessingDlg}
          onClose={() => setOpenSwapProcessingDlg(false)}
        />
      )}
    </Container>
  );
};

export default SwapCard;
